import { useState } from 'react';
import { astrologyReviews } from '../../../mocks/astrologyReviews';

const FILTER_TABS = [
  { id: 'all', label: '전체', count: astrologyReviews.length },
  { id: '점성술 리딩', label: '점성술 리딩', count: astrologyReviews.filter((r) => r.tag === '점성술 리딩').length },
  { id: '결정 비서', label: '결정 비서', count: astrologyReviews.filter((r) => r.tag === '결정 비서').length },
];

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'xs' }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className={`${size === 'sm' ? 'w-3.5 h-3.5' : 'w-3 h-3'} flex items-center justify-center`}>
          <i className={`ri-star-${i < rating ? 'fill' : 'line'} text-amber-400 ${size === 'sm' ? 'text-xs' : 'text-[10px]'}`}></i>
        </div>
      ))}
    </div>
  );
}

export default function AstrologyReviewSection() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const filtered = activeFilter === 'all'
    ? astrologyReviews
    : astrologyReviews.filter((r) => r.tag === activeFilter);

  const avgRating = (astrologyReviews.reduce((s, r) => s + r.rating, 0) / astrologyReviews.length).toFixed(1);

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section className="py-20 px-4 sm:px-6 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-900/8 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-900/6 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/25 text-purple-300 text-xs tracking-widest uppercase mb-5 font-semibold">
            <i className="ri-star-smile-line text-sm"></i>
            <span>실제 이용 후기</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">
            점성술 · 결정비서
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-gold via-amber-300 to-primary-gold ml-2">이용 후기</span>
          </h2>
          <p className="text-purple-300/70 text-base max-w-xl mx-auto">
            별자리 탄생 차트부터 인생의 중대 결정까지, 실제 사용자들의 이야기를 들어보세요
          </p>

          {/* Rating Summary */}
          <div className="flex items-center justify-center gap-8 mt-8">
            <div className="text-center">
              <div className="flex items-baseline gap-1 justify-center">
                <span className="text-4xl font-black text-primary-gold">{avgRating}</span>
                <span className="text-primary-gold/70 text-lg font-bold">/ 5.0</span>
              </div>
              <StarRating rating={5} />
              <p className="text-purple-400/60 text-xs mt-1">종합 만족도</p>
            </div>
            <div className="w-px h-12 bg-white/10" />
            <div className="text-center">
              <p className="text-3xl font-black text-white">2,400+</p>
              <p className="text-purple-400/60 text-xs mt-1">누적 리뷰</p>
            </div>
            <div className="w-px h-12 bg-white/10" />
            <div className="text-center">
              <p className="text-3xl font-black text-white">98%</p>
              <p className="text-purple-400/60 text-xs mt-1">재방문율</p>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex p-1 rounded-2xl bg-white/5 border border-white/10 gap-1">
            {FILTER_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeFilter === tab.id
                    ? 'bg-gradient-to-r from-primary-gold/20 to-amber-500/15 border border-primary-gold/40 text-white'
                    : 'text-purple-400/60 hover:text-purple-200'
                }`}
              >
                <span>{tab.label}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-xs ${activeFilter === tab.id ? 'bg-primary-gold/20 text-primary-gold' : 'bg-white/10 text-purple-400/50'}`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Review Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((review) => {
            const isExpanded = expandedIds.has(review.id);
            const isLong = review.content.length > 130;
            return (
              <div
                key={review.id}
                className="group rounded-2xl bg-gradient-to-br from-primary-dark/70 via-mystic-dark/60 to-primary-dark/70 border border-white/8 hover:border-primary-gold/25 transition-all duration-300 overflow-hidden flex flex-col"
              >
                {/* Tag + Rating */}
                <div className="flex items-center justify-between px-5 pt-4 pb-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${review.tagColor}`}>
                    {review.tag}
                  </span>
                  <StarRating rating={review.rating} size="xs" />
                </div>

                {/* Content */}
                <div className="px-5 pb-4 flex-1">
                  {/* Highlight */}
                  <div className="flex items-start gap-2 mb-3 p-2.5 rounded-xl bg-primary-gold/8 border border-primary-gold/15">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-double-quotes-l text-primary-gold/70 text-xs"></i>
                    </div>
                    <p className="text-primary-gold/90 text-xs font-semibold leading-tight">{review.highlight}</p>
                  </div>

                  {/* Review text */}
                  <p className={`text-purple-200/80 text-sm leading-relaxed ${!isExpanded && isLong ? 'line-clamp-3' : ''}`}>
                    {review.content}
                  </p>
                  {isLong && (
                    <button
                      onClick={() => toggleExpand(review.id)}
                      className="mt-1.5 text-primary-gold/70 text-xs font-semibold hover:text-primary-gold transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {isExpanded ? '접기 ▲' : '더 보기 ▼'}
                    </button>
                  )}
                </div>

                {/* Author */}
                <div className="px-5 pb-4 flex items-center gap-3 border-t border-white/6 pt-3">
                  <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 border border-primary-gold/20">
                    <img
                      src={review.image}
                      alt={review.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm leading-none">{review.name}</p>
                    <p className="text-purple-400/60 text-xs mt-0.5">{review.role}</p>
                  </div>
                  <span className="text-purple-500/40 text-xs flex-shrink-0">{review.date}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom note */}
        <div className="text-center mt-8">
          <p className="text-purple-500/50 text-xs">
            <i className="ri-shield-check-line mr-1.5 text-primary-gold/40"></i>
            모든 후기는 실제 서비스 이용 후 작성된 인증 리뷰입니다
          </p>
        </div>
      </div>
    </section>
  );
}
